/**
 * xnovate-product-animations.js
 * GSAP animations for the Keynova One product page.
 * Requires: gsap.min.js, ScrollTrigger.min.js (loaded via theme.liquid with defer)
 */

(function () {
  'use strict';

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      setTimeout(init, 100);
      return;
    }
    gsap.registerPlugin(ScrollTrigger);

    if (!prefersReducedMotion) {
      setupHero();
      setupValue();
      setupBureau();
      setupFeatures();
      setupDemonstration();
      setupStory();
      setupApps();
      setupSpecs();
      setupCtaFinal();
    }
    setupValueCarousel();
    setupStickyATC();
    setupFAQClosing();
    // V11.3.2 — chantier 2 : init collapse Specs mobile. Place HORS du
    // bloc !prefersReducedMotion car la fonction est non-motion (pure
    // toggle de classe au click). prefers-reduced-motion users doivent
    // pouvoir deplier les specs aussi.
    setupSpecsCollapse();
    // V11.2.7 — chantier 1 : carrousel Features fonctionne sans depender de
    // GSAP (pas de prefersReducedMotion guard car le drag est neutre cote UX).
    setupFeaturesCarousel();
    // V11.4.6 : lightbox Galerie (remplace setupGalleryCarousel V11.4.4 —
    // refonte scroll horizontal -> Bento grid + lightbox plein ecran).
    setupGalleryLightbox();
  }

  /* ----------------------------------------
     HERO — V11.2.6 chantier 1 : entrance overlay corrige.
     - Video : zoom-in subtil sur load (scale 1.05 -> 1.0, duration 1.4s)
     - Content stagger reveal au load (y 24 -> 0, opacity 0 -> 1, stagger 0.1s)
     - Parallax scroll retire : V11.2.5 yPercent -8 sur la video aurait
       expose une bande noire du fond __media car la video est desormais
       contained 16:9 dans un wrapper (vs full-bleed V11.2.5). Le mouvement
       de la video autoplay loop suffit a la vie visuelle sans scroll parallax.
  ---------------------------------------- */
  function setupHero() {
    if (!document.querySelector('.keynova-hero')) return;
    var tl = gsap.timeline({ defaults: { ease: 'power2.out' } });
    tl.from('.keynova-hero__video', { scale: 1.05, opacity: 0, duration: 1.4 }, 0)
      .from('.keynova-hero__content > *', { y: 24, opacity: 0, stagger: 0.1, duration: 0.7 }, 0.3);
  }

  /* ----------------------------------------
     VALUE — stagger reveal on scroll
  ---------------------------------------- */
  function setupValue() {
    if (!document.querySelector('.keynova-value')) return;
    ScrollTrigger.batch('[data-keynova-value-block]', {
      onEnter: function (batch) {
        gsap.from(batch, {
          y: 32,
          opacity: 0,
          stagger: 0.12,
          duration: 0.6,
          ease: 'power2.out',
          onComplete: function () {
            // V10.5 — FIX H6 : force etat final pour eviter qu'une card reste
            // bloquee en gris pale si ScrollTrigger n'a pas complete la trans.
            batch.forEach(function (el) {
              gsap.set(el, { opacity: 1, y: 0, clearProps: 'transform' });
              el.classList.add('is-revealed');
            });
          }
        });
      },
      start: 'top 85%',
      once: true
    });
  }

  /* ----------------------------------------
     BUREAU — V11.2.5 chantier 5 : enrichissement narratif.
     V11.2.3 (glow fade-in + photo zoom scrub) conserve.
     Nouveau V11.2.5 :
     - Overline simple fade-in (top 75%)
     - H2 mot-par-mot via classList .is-active sur .xn-bureau__h2-word (stagger
       120ms par mot, ScrollTrigger top 70%)
     - Body line-by-line via classList .is-active sur .xn-bureau__line (stagger
       250ms par ligne + delai initial 600ms pour demarrer apres le H2,
       ScrollTrigger top 60%)
     - Safety net 3s pour forcer la visibilite finale
  ---------------------------------------- */
  /* ----------------------------------------
     BUREAU — V11.2.7 chantier 7 : refonte overlay simplifie.
     Markup refondu (photo full-bleed + overlay top-right), plus de glow ni
     pulse ni photo zoom GSAP scrub ni split mot/ligne. Simple reveal stagger
     sur 3 elements (overline + H2 + body) au scroll-in.
     - gsap.set initial opacity 0 + y 24
     - ScrollTrigger top 75% once -> stagger 180ms, duration 0.7s, ease power2.out
     - Safety net 2s pour forcer visibilite
  ---------------------------------------- */
  function setupBureau() {
    var section = document.querySelector('.xn-bureau');
    if (!section) return;

    var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var elements = [
      section.querySelector('.xn-overline'),
      section.querySelector('.xn-bureau__h2'),
      section.querySelector('.xn-bureau__body')
    ].filter(Boolean);

    if (!elements.length) return;

    if (prefersReduced) {
      gsap.set(elements, { opacity: 1, y: 0 });
      return;
    }

    gsap.set(elements, { opacity: 0, y: 24 });

    ScrollTrigger.create({
      trigger: section,
      start: 'top 75%',
      once: true,
      onEnter: function () {
        gsap.to(elements, {
          opacity: 1,
          y: 0,
          duration: 0.7,
          stagger: 0.18,
          ease: 'power2.out'
        });
      }
    });

    setTimeout(function () {
      elements.forEach(function (el) {
        if (parseFloat(getComputedStyle(el).opacity) < 0.1) {
          gsap.set(el, { opacity: 1, y: 0 });
        }
      });
    }, 2000);
  }

  /* ----------------------------------------
     FEATURES — V11.2.5 chantier 1 : pattern classList .is-revealed.
     Remplace gsap.set + gsap.to V11.2.0 qui posait inline style opacity: 1
     et ecrasait l'opacity 0.65 du repos CSS (chantier 1 V11.2.3 hover casse).
     - Etat initial : CSS .keynova-features__card (opacity 0, y 28)
     - Scroll-in : ScrollTrigger.batch ajoute .is-revealed avec stagger 120ms
     - CSS gere les transitions vers opacity 0.65 + y 0
     - :hover -> opacity 1, transition 320ms (CSS pur, plus de conflit GSAP)
     - Safety net 2s pour forcer .is-revealed si batch n'a pas tire
  ---------------------------------------- */
  function setupFeatures() {
    var cards = gsap.utils.toArray('.keynova-features__card');
    if (!cards.length) return;

    ScrollTrigger.batch(cards, {
      start: 'top 85%',
      once: true,
      onEnter: function (batch) {
        batch.forEach(function (card, i) {
          setTimeout(function () { card.classList.add('is-revealed'); }, i * 120);
        });
      }
    });

    setTimeout(function () {
      cards.forEach(function (c) {
        if (!c.classList.contains('is-revealed')) {
          c.classList.add('is-revealed');
        }
      });
    }, 2000);
  }

  /* ----------------------------------------
     FEATURES CAROUSEL — V11.2.7 chantier 1.
     Solution au probleme V11.2.6 : Lenis smooth scroll global intercepte
     wheel + touch et empechait le scroll horizontal natif du carrousel.
     Combinaison de 3 mecanismes pour robustesse :
     - data-lenis-prevent-horizontal (markup) : Lenis convention pour
       exempter les events horizontaux de l'interception.
     - Drag pointer-based (this fn) : clic-maintenu-glisser souris + tactile.
     - Clic navigation prev/next : clic sur card a >66% du grid -> avance ;
       clic sur card a <34% -> recule. Card au centre = clic neutre.
     Anti-faux-clic via hasMoved : un drag-then-release ne declenche pas
     l'event click meme s'il finit sur une card.
     Conserve setupFeatures() pour le reveal stagger V11.2.6 (separe).
  ---------------------------------------- */
  function setupFeaturesCarousel() {
    var grid = document.querySelector('[data-keynova-features-carousel]');
    if (!grid) return;

    var cards = Array.prototype.slice.call(grid.querySelectorAll('.keynova-features__card'));
    if (!cards.length) return;

    var isDown = false;
    var startX = 0;
    var scrollStart = 0;
    var hasMoved = false;
    var lastX = 0;
    var lastTime = 0;
    var velocity = 0;
    // V11.2.9 — chantier 1.1 : moving average sur 5 derniers samples pour
    // lisser la velocity. La V11.2.8 calculait velocity instantanee (1 frame),
    // ce qui oscillait fortement en cas de dt court -> momentum erratique.
    var velocityHistory = [];
    var rubbering = false;   // V14.1 (§5.4b) — rubber-band actif (pas de débordement / bord)
    var userInteracted = false;  // v14.2.3 (#2) — true dès le 1er drag → gèle le re-align auto

    // V11.2.9 — chantier 1.1 : pointerup/cancel ecoutes sur document (vs grid
    // V11.2.8) pour catcher les releases hors du grid. pointerleave retire
    // (declenchait endDrag au moindre passage sur la marge -> sensation saccade).
    function attachDocListeners() {
      document.addEventListener('pointerup', endDrag);
      document.addEventListener('pointercancel', endDrag);
    }
    function detachDocListeners() {
      document.removeEventListener('pointerup', endDrag);
      document.removeEventListener('pointercancel', endDrag);
    }

    grid.addEventListener('pointerdown', function (e) {
      isDown = true;
      hasMoved = false;
      userInteracted = true;  // v14.2.3 (#2) — l'utilisateur prend la main
      startX = e.pageX - grid.offsetLeft;
      scrollStart = grid.scrollLeft;
      lastX = e.pageX;
      lastTime = performance.now();
      velocity = 0;
      velocityHistory = [];
      // Stoppe toute animation momentum/snap en cours pour rendre la main au drag.
      if (grid._momentumTween) {
        grid._momentumTween.kill();
        grid._momentumTween = null;
      }
      grid.style.cursor = 'grabbing';
      try { grid.setPointerCapture(e.pointerId); } catch (err) {}
      attachDocListeners();
    });

    grid.addEventListener('pointermove', function (e) {
      if (!isDown) return;
      var x = e.pageX - grid.offsetLeft;
      // V11.2.9 — chantier 1.1 : multiplicateur 1.5 -> 1.0. Mouvement 1:1 entre
      // pointeur et scrollLeft, feeling plus naturel (1.5 V11.2.7-V11.2.8 etait
      // survolte, sensation d'amplification artificielle).
      var walk = (x - startX) * 1.0;
      // V11.3.1 — chantier 1 : preventDefault conditionne sur drag horizontal
      // detecte (>8px). Avant cette V, e.preventDefault etait appele a chaque
      // pointermove des isDown=true, bloquant potentiellement le scroll natif
      // de la page sur swipe vertical mobile. Maintenant, on ne bloque le
      // default que si le drag horizontal est confirme. Combine avec
      // touch-action: pan-y CSS (sur .keynova-features__grid) pour autoriser
      // le scroll vertical natif page.
      if (Math.abs(walk) > 8) {
        hasMoved = true;
        e.preventDefault();
      }
      grid.scrollLeft = scrollStart - walk;
      // V14.1 (§5.4b) — rubber-band : sans débordement (ou aux bords), les cartes
      // restent « attrapables » et glissent légèrement (effet vivant, pas figé).
      var maxScrollNow = grid.scrollWidth - grid.clientWidth;
      var desired = scrollStart - walk;
      var over = desired < 0 ? desired : (desired > maxScrollNow ? desired - maxScrollNow : 0);
      if (over !== 0) {
        hasMoved = true;
        rubbering = true;
        var rb = -over * 0.32;
        if (rb > 64) rb = 64; else if (rb < -64) rb = -64;
        grid.style.setProperty('--feat-rubber', rb.toFixed(1) + 'px');
      } else if (rubbering) {
        grid.style.setProperty('--feat-rubber', '0px');
      }
      // V11.2.9 — chantier 1.1 : velocity lissee (moving average 5 samples).
      // Signe inverse : scrollLeft va a l'oppose du deplacement pointeur.
      var now = performance.now();
      var dt = now - lastTime;
      if (dt > 0) {
        var instantV = -(e.pageX - lastX) / dt;
        velocityHistory.push(instantV);
        if (velocityHistory.length > 5) velocityHistory.shift();
        var sum = 0;
        for (var i = 0; i < velocityHistory.length; i++) sum += velocityHistory[i];
        velocity = sum / velocityHistory.length;
      }
      lastX = e.pageX;
      lastTime = now;
    });

    function endDrag(e) {
      if (!isDown) return;
      isDown = false;
      grid.style.cursor = '';
      detachDocListeners();
      if (e && e.pointerId !== undefined) {
        try { grid.releasePointerCapture(e.pointerId); } catch (err) {}
      }
      // Momentum au pointerup. Seuil 0.05 px/ms, 600ms d'inertie projetee,
      // anime sur 0.9s avec ease power2.out (V11.2.8 valeurs conservees).
      // V11.2.13 — chantier 1 : VRAIE cause racine snap V11.2.9-V11.2.12.
      // ScrollToPlugin GSAP n'est pas charge dans le projet (verifie : seul
      // ScrollTrigger.min.js dans assets/). Sans ce plugin, gsap.to({ scrollLeft })
      // peut ne pas animer fiablement la propriete DOM scrollLeft d'un element.
      // Solution portable : animer un objet proxy { val } via onUpdate qui
      // assigne grid.scrollLeft = obj.val a chaque frame. Independant de tout
      // plugin GSAP supplementaire.
      if (Math.abs(velocity) > 0.05 && typeof gsap !== 'undefined') {
        var targetScroll = grid.scrollLeft + velocity * 600;
        var scrollObj = { val: grid.scrollLeft };
        grid._momentumTween = gsap.to(scrollObj, {
          val: targetScroll,
          duration: 0.9,
          ease: 'power2.out',
          onUpdate: function () { grid.scrollLeft = scrollObj.val; },
          onComplete: function () { grid._momentumTween = null; }
        });
      }
      // V14.1 (§5.4b) — relâche le rubber-band : retour élastique à 0.
      if (rubbering && typeof gsap !== 'undefined') {
        rubbering = false;
        var rbObj = { v: parseFloat(grid.style.getPropertyValue('--feat-rubber')) || 0 };
        gsap.to(rbObj, { v: 0, duration: 0.55, ease: 'elastic.out(1, 0.6)',
          onUpdate: function () { grid.style.setProperty('--feat-rubber', rbObj.v.toFixed(2) + 'px'); } });
      } else if (rubbering) {
        rubbering = false;
        grid.style.setProperty('--feat-rubber', '0px');
      }
      velocity = 0;
    }

    // V11.2.9 — chantier 1.2 : clic carte = elle se centre dans le viewport
    // du grid (remplace la logique zonale 34/66% prev/next V11.2.7-V11.2.8).
    // Tout clic sur n'importe quelle carte la centre, y compris la 4e
    // partiellement coupee a droite -> revele son contenu complet.
    // V11.2.12 — chantier 1 : fix bug ne fonctionnait pas V11.2.9-V11.2.11.
    // card.offsetLeft renvoyait position absolue depuis body (offsetParent
    // du card etait body faute de position:relative sur grid -- corrige CSS
    // V11.2.12). Passage a getBoundingClientRect + grid.scrollLeft : calcul
    // independant de la cascade offsetParent, robuste a tous les contextes.
    // V11.3 — chantier 1 : delegation click au grid via closest. Cause racine
    // identifiee apres 5 iterations V11.2.9-V11.2.13 : setPointerCapture au
    // pointerdown peut dispatcher le click event sur le grid (qui a la capture)
    // plutot que sur la card cible, faisant que card.addEventListener('click')
    // ne se declenche jamais. Solution : un seul listener sur le grid, on
    // detecte la card cliquee via e.target.closest('.keynova-features__card').
    // Cela couvre les 2 scenarios (capture active ou pas).
    grid.addEventListener('click', function (e) {
      if (hasMoved) {
        e.preventDefault();
        return;
      }
      var card = e.target.closest('.keynova-features__card');
      if (!card) return;
      if (typeof gsap === 'undefined') return;
      if (grid._momentumTween) {
        grid._momentumTween.kill();
        grid._momentumTween = null;
      }
      var gridRect = grid.getBoundingClientRect();
      var cardRect = card.getBoundingClientRect();
      var cardLeftInGrid = (cardRect.left - gridRect.left) + grid.scrollLeft;
      var targetScroll = cardLeftInGrid - (grid.clientWidth / 2) + (card.offsetWidth / 2);
      var maxScroll = grid.scrollWidth - grid.clientWidth;
      if (targetScroll < 0) targetScroll = 0;
      if (targetScroll > maxScroll) targetScroll = maxScroll;
      // V11.2.13 — chantier 1 : meme fix proxy object que momentum (cf. endDrag
      // ci-dessus). gsap.to({ scrollLeft }) inoperant sans ScrollToPlugin --
      // on anime un objet { val } et reporte sur grid.scrollLeft via onUpdate.
      var scrollObj = { val: grid.scrollLeft };
      grid._momentumTween = gsap.to(scrollObj, {
        val: targetScroll,
        duration: 0.6,
        ease: 'power2.inOut',
        onUpdate: function () { grid.scrollLeft = scrollObj.val; },
        onComplete: function () { grid._momentumTween = null; }
      });
    });

    // v14.2.3 (#2) — Cartes fantômes : le fantôme de TÊTE décale la 1ère carte
    // réelle à scrollLeft 0. On initialise donc le scroll pour que « profils »
    // repose alignée sur le logo (.xn-container, cf. #1) ; le fantôme offre la
    // marge de glissement à gauche (centrage de la 1ère carte possible), le
    // fantôme de queue la marge à droite. La différence d'offsetLeft
    // (carte − fantôme) = largeur fantôme + gap, indépendante du padding-left calc.
    var headGhost = grid.querySelector('.keynova-features__ghost');
    function alignFirstCard() {
      if (!headGhost) return;
      // Carrousel actif uniquement s'il y a débordement horizontal (desktop ≥969px).
      if (grid.scrollWidth <= grid.clientWidth + 1) return;
      var offset = cards[0].offsetLeft - headGhost.offsetLeft;
      if (offset > 0) grid.scrollLeft = offset;
    }
    alignFirstCard();
    // v14.2.3 (#2) — re-calage après chargement de la police variable : un reflow de
    // fonte ne déclenche pas 'resize'. Idempotent (largeurs de carte/fantôme fixes),
    // gelé dès la 1ère interaction.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { if (!userInteracted) alignFirstCard(); });
    }
    // Re-alignement au resize tant que l'utilisateur n'a pas pris la main
    // (couvre le passage tablette→desktop). rAF-throttle, aucune dépendance.
    var alignRaf = null;
    window.addEventListener('resize', function () {
      if (userInteracted) return;
      if (alignRaf) cancelAnimationFrame(alignRaf);
      alignRaf = requestAnimationFrame(alignFirstCard);
    });

    grid.style.cursor = 'grab';
  }

  /* ----------------------------------------
     GALLERY LIGHTBOX — V11.4.6
     Remplace setupGalleryCarousel (scroll horizontal V11.4.4) par un lightbox
     plein ecran declenche au click sur une tuile du Bento grid.

     Fonctions :
       - Click / Enter / Espace sur tuile -> ouvre lightbox a l'index
       - Prev/next : boutons + clavier ArrowLeft/Right + swipe mobile
       - Fermeture : croix + ESC + click backdrop
       - Compteur N / total
       - Body scroll lock pendant ouverture
       - Focus trap simple + restitution focus a la fermeture (accessibilite)
  ---------------------------------------- */
  function setupGalleryLightbox() {
    var gallery = document.querySelector('[data-keynova-gallery]');
    if (!gallery) return;

    var items = Array.prototype.slice.call(gallery.querySelectorAll('.keynova-gallery__item'));
    var lightbox = gallery.querySelector('[data-gallery-lightbox]');
    if (!items.length || !lightbox) return;

    var lbImage = lightbox.querySelector('[data-lightbox-image]');
    var lbCounter = lightbox.querySelector('[data-lightbox-counter]');
    var btnPrev = lightbox.querySelector('[data-lightbox-prev]');
    var btnNext = lightbox.querySelector('[data-lightbox-next]');
    var btnClose = lightbox.querySelector('[data-lightbox-close]');

    // Collecte les sources depuis les <img> des tuiles (src + alt)
    var sources = items.map(function (item) {
      var img = item.querySelector('img');
      return { src: img.getAttribute('src'), alt: img.getAttribute('alt') || '' };
    });

    var currentIndex = 0;
    var lastFocused = null;

    function updateLightbox() {
      var item = sources[currentIndex];
      lbImage.src = item.src;
      lbImage.alt = item.alt;
      if (lbCounter) lbCounter.textContent = (currentIndex + 1) + ' / ' + sources.length;
    }

    function openLightbox(index) {
      currentIndex = index;
      lastFocused = document.activeElement;
      updateLightbox();
      lightbox.classList.add('is-open');
      lightbox.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      if (btnClose) btnClose.focus();
      document.addEventListener('keydown', onKeydown);
    }

    function closeLightbox() {
      lightbox.classList.remove('is-open');
      lightbox.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKeydown);
      if (lastFocused && lastFocused.focus) lastFocused.focus();
    }

    function next() {
      currentIndex = (currentIndex + 1) % sources.length;
      updateLightbox();
    }

    function prev() {
      currentIndex = (currentIndex - 1 + sources.length) % sources.length;
      updateLightbox();
    }

    function onKeydown(e) {
      if (e.key === 'Escape') {
        closeLightbox();
      } else if (e.key === 'ArrowRight') {
        next();
      } else if (e.key === 'ArrowLeft') {
        prev();
      } else if (e.key === 'Tab') {
        // Focus trap simple entre les boutons du lightbox
        var focusables = lightbox.querySelectorAll('button');
        if (!focusables.length) return;
        var first = focusables[0];
        var last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    // Click + clavier sur les tuiles
    items.forEach(function (item, i) {
      item.addEventListener('click', function () { openLightbox(i); });
      item.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openLightbox(i);
        }
      });
    });

    if (btnNext) btnNext.addEventListener('click', next);
    if (btnPrev) btnPrev.addEventListener('click', prev);
    if (btnClose) btnClose.addEventListener('click', closeLightbox);

    // Click sur le backdrop (hors image, hors boutons) ferme
    lightbox.addEventListener('click', function (e) {
      if (e.target === lightbox || e.target.classList.contains('keynova-lightbox__stage')) {
        closeLightbox();
      }
    });

    // Swipe mobile
    var touchStartX = 0;
    var touchStartY = 0;
    lightbox.addEventListener('touchstart', function (e) {
      touchStartX = e.changedTouches[0].screenX;
      touchStartY = e.changedTouches[0].screenY;
    }, { passive: true });
    lightbox.addEventListener('touchend', function (e) {
      var dx = e.changedTouches[0].screenX - touchStartX;
      var dy = e.changedTouches[0].screenY - touchStartY;
      // Swipe horizontal dominant et significatif
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
        if (dx < 0) next();
        else prev();
      }
    }, { passive: true });
  }

  /* ----------------------------------------
     DEMONSTRATION 360 - V11.4.1 scroll-synced
     Refactor : remplace le pattern V11.2.x video-bg + poster par un canvas
     qui dessine une frame du rendu 360 du clavier indexee par scroll progress.

     Preservations V11.3.x (anti-regression critique) :
       - ScrollTrigger.create avec pin + snap 1/(N-1) (V11.3.4)
       - 6 fragments crossfade avec is-active / is-leaving (V11.2.x)
       - remeasureFragments + resize listener debounced 200ms (V11.3.1)
       - prefers-reduced-motion early return (V11.3.2)
       - Mobile parite desktop (no bypass V11.3.2)

     Nouveautes V11.4.1 :
       - Preload 61 frames keynova-360-000.webp a keynova-360-060.webp
       - Frame 0 chargee en priorite (LCP) via <img poster-fallback>,
         1-60 lazy apres IO rootMargin 50%
       - onUpdate combine setActiveFragment(progress) + drawFrame(progress)
       - drawFrame : interpolation lineaire progress -> frameIndex round(progress * 60)
       - Bypass canvas si perfTier === 'lite' ou prefers-reduced-motion
         (poster-fallback statique reste visible, setupScrollTriggerFragmentsOnly
          continue d'animer les 6 fragments)
       - Canvas resize handler : ajuste canvas.width/height aux pixels physiques
         viewport courant + devicePixelRatio (limite a 2x) pour eviter blur dpr
  ---------------------------------------- */
  function setupDemonstration() {
    var section = document.querySelector('.keynova-demonstration');
    if (!section) return;

    var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return; // poster-fallback statique suffit, pas de scrollytelling

    var scrollZone = section.querySelector('[data-demonstration-scroll-zone]');
    var pin = section.querySelector('[data-demonstration-pin]');
    var fragments = gsap.utils.toArray('.keynova-demonstration__fragment');
    var canvas = section.querySelector('[data-demo-360-canvas]');
    var poster = section.querySelector('[data-demo-360-poster]');

    if (!scrollZone || !pin || !fragments.length) return;

    /* ---------- Setup fragments (PRESERVATION V11.3.x exacte) ---------- */
    var stage = section.querySelector('.keynova-demonstration__fragments-stage');
    var fragmentHeights = [];
    var currentIndex = 0;

    function remeasureFragments() {
      fragmentHeights = [];
      fragments.forEach(function (frag) {
        fragmentHeights.push(frag.getBoundingClientRect().height);
      });
      if (stage && fragmentHeights[currentIndex] !== undefined) {
        stage.style.setProperty('--fragment-active-height', fragmentHeights[currentIndex] + 'px');
      }
    }
    remeasureFragments();

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        remeasureFragments();
        resizeCanvas();
      }, 200);
    });

    fragments.forEach(function (f, i) {
      f.classList.toggle('is-active', i === 0);
    });

    function setActiveFragment(targetIndex) {
      targetIndex = Math.max(0, Math.min(fragments.length - 1, Math.floor(targetIndex)));
      if (targetIndex === currentIndex) return;

      var leaving = fragments[currentIndex];
      var entering = fragments[targetIndex];

      if (leaving) {
        leaving.classList.remove('is-active');
        if (targetIndex > currentIndex) {
          leaving.classList.add('is-leaving');
          setTimeout(function () { leaving.classList.remove('is-leaving'); }, 1500);
        }
      }

      if (entering) {
        entering.classList.remove('is-leaving');
        void entering.offsetHeight;
        entering.classList.add('is-active');
      }

      currentIndex = targetIndex;
      if (stage && fragmentHeights[targetIndex] !== undefined) {
        stage.style.setProperty('--fragment-active-height', fragmentHeights[targetIndex] + 'px');
      }
    }

    /* ---------- setupScrollTriggerFragmentsOnly (perf-lite bypass) ---------- */
    function setupScrollTriggerFragmentsOnly() {
      ScrollTrigger.create({
        trigger: scrollZone,
        start: 'top top',
        end: 'bottom bottom',
        pin: pin,
        pinSpacing: true,
        invalidateOnRefresh: true,
        snap: {
          snapTo: 1 / (fragments.length - 1),
          duration: { min: 0.2, max: 0.5 },
          delay: 0.05,
          ease: 'power1.inOut'
        },
        onUpdate: function (self) {
          var targetIndex = Math.round(self.progress * (fragments.length - 1));
          setActiveFragment(targetIndex);
        }
      });
    }

    /* ---------- 360 canvas setup (V11.4.1 nouveau) ---------- */

    // detectPerfTier() V11.3.1 marque le body via xn-perf-mid / xn-perf-lite
    // (set au mount du script xnovate-animations.js). On bypass le preload
    // canvas si lite : poster-fallback statique reste visible, fragments
    // continuent de scroller via setupScrollTriggerFragmentsOnly.
    var isPerfLite = document.body.classList.contains('xn-perf-lite');

    if (isPerfLite || !canvas) {
      setupScrollTriggerFragmentsOnly();
      return;
    }

    var ctx = canvas.getContext('2d');
    var FRAME_COUNT = 61;
    var FRAME_BASE_URL = '';   // prefixe URL CDN, parse via setupFrameUrls
    var FRAME_SUFFIX = '';     // suffixe .webp?v=...
    var frames = [];           // Array<Image> de 61 elements
    var framesLoaded = 0;
    var canvasReady = false;

    // V11.4.1 — Parse l'URL Shopify CDN du <img poster-fallback> pour
    // reconstruire les URLs des 60 autres frames.
    // Pattern Shopify : ...cdn.shopify.com/.../assets/keynova-360-000.webp?v=...
    // Regex capture le prefix + le suffix pour les inserer autour du numero
    // de frame (000, 001, ..., 060) reconstruit par padStart 3 zeros.
    function setupFrameUrls() {
      if (!poster || !poster.src) return false;
      var match = poster.src.match(/(.+keynova-360-)000(\.webp.*)$/);
      if (!match) return false;
      FRAME_BASE_URL = match[1];
      FRAME_SUFFIX = match[2];

      for (var i = 0; i < FRAME_COUNT; i++) {
        var img = new Image();
        var num = ('00' + i).slice(-3); // 000, 001, ..., 060
        img.dataset.src = FRAME_BASE_URL + num + FRAME_SUFFIX;
        frames.push(img);
      }
      return true;
    }

    if (!setupFrameUrls()) {
      // Echec parsing URL : bypass canvas, garde poster statique + fragments
      setupScrollTriggerFragmentsOnly();
      return;
    }

    // Frame 0 deja chargee par le navigateur via <img poster-fallback eager>.
    // On la met dans frames[0] et on la dessine immediatement.
    frames[0].src = frames[0].dataset.src;
    frames[0].addEventListener('load', function () {
      framesLoaded++;
      drawFrame(0);
      canvasReady = true;
      canvas.classList.add('is-ready');
    });
    // Si frame 0 deja en cache navigateur (le poster-fallback a deja
    // initie le download), l'event load peut etre rate. Re-essai immediat.
    if (frames[0].complete && frames[0].naturalWidth > 0) {
      framesLoaded++;
      drawFrame(0);
      canvasReady = true;
      canvas.classList.add('is-ready');
    }

    function resizeCanvas() {
      if (!canvas) return;
      var rect = canvas.getBoundingClientRect();
      // devicePixelRatio limite a 2 pour eviter de doubler memory cost
      // sur ecrans high-dpi (iPhone Pro = 3 dpr -> sinon canvas 4K).
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      if (canvasReady) {
        // Redessine la frame courante au nouveau format.
        var idx = Math.round((currentIndex / (fragments.length - 1)) * (FRAME_COUNT - 1));
        drawFrame(idx);
      }
    }
    resizeCanvas();

    function drawFrame(frameIndex) {
      frameIndex = Math.max(0, Math.min(FRAME_COUNT - 1, Math.floor(frameIndex)));
      var img = frames[frameIndex];
      if (!img || !img.complete || img.naturalWidth === 0) {
        // Frame demandee pas encore chargee : on cherche la plus proche
        // qui est chargee pour eviter un canvas vide. Effet transitoire
        // acceptable pendant que les frames se preloadent (utilisateur
        // voit la rotation se "rattraper" frame par frame).
        var nearest = findNearestLoadedFrame(frameIndex);
        if (nearest >= 0) img = frames[nearest];
        else return;
      }
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    }

    function findNearestLoadedFrame(targetIndex) {
      for (var d = 0; d < FRAME_COUNT; d++) {
        var lower = targetIndex - d;
        var upper = targetIndex + d;
        if (lower >= 0 && frames[lower].complete && frames[lower].naturalWidth > 0) return lower;
        if (upper < FRAME_COUNT && frames[upper].complete && frames[upper].naturalWidth > 0) return upper;
      }
      return -1;
    }

    function preloadAllFrames() {
      for (var i = 1; i < FRAME_COUNT; i++) {
        if (frames[i].src) continue; // deja initie
        frames[i].src = frames[i].dataset.src;
        frames[i].addEventListener('load', onFrameLoaded);
      }
    }

    function onFrameLoaded() {
      framesLoaded++;
      if (framesLoaded >= FRAME_COUNT && window.ScrollTrigger) {
        // Toutes les frames chargees -> refresh ScrollTrigger pour
        // s'assurer que la frame courante est dessinee proprement.
        ScrollTrigger.refresh();
      }
    }

    // Declenche preload 1-60 quand la section approche du viewport
    // (rootMargin 50% = ~ une demi-viewport avant l'arrivee section).
    if ('IntersectionObserver' in window) {
      var preloadIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            preloadAllFrames();
            preloadIO.unobserve(section);
          }
        });
      }, { rootMargin: '50% 0px' });
      preloadIO.observe(section);
    } else {
      preloadAllFrames();
    }

    /* ---------- ScrollTrigger main (V11.3.4 snap + V11.4.1 drawFrame) ---------- */
    ScrollTrigger.create({
      trigger: scrollZone,
      start: 'top top',
      end: 'bottom bottom',
      pin: pin,
      pinSpacing: true,
      invalidateOnRefresh: true,
      // V11.3.4 — snap natif ScrollTrigger sur 1/(N-1) = sensation cliquet
      // controle, identique desktop + mobile, prefers-reduced-motion deja
      // gere par early return.
      snap: {
        snapTo: 1 / (fragments.length - 1),
        duration: { min: 0.2, max: 0.5 },
        delay: 0.05,
        ease: 'power1.inOut'
      },
      onUpdate: function (self) {
        var progress = self.progress;
        // Fragment scrollytelling (preservation V11.3.4 exacte)
        var targetFragIndex = Math.round(progress * (fragments.length - 1));
        setActiveFragment(targetFragIndex);
        // 360 frame drawing (V11.4.1 nouveau) - mapping lineaire
        // progress [0..1] -> frameIndex [0..60]
        var targetFrameIndex = Math.round(progress * (FRAME_COUNT - 1));
        drawFrame(targetFrameIndex);
      }
    });
  }

  /* ----------------------------------------
     STORY — alternating photo/text blocks
     (replaces old GALLERY parallax)
  ---------------------------------------- */
  function setupStory() {
    var blocks = gsap.utils.toArray('[data-keynova-story-block]');
    if (!blocks.length) return;

    var isDesktop = window.matchMedia('(min-width: 768px)').matches;

    blocks.forEach(function (block, index) {
      var media = block.querySelector('.keynova-story__media');
      var img = block.querySelector('.keynova-story__media img');
      var textChildren = block.querySelectorAll('.keynova-story__text > *');

      if (!media || !textChildren.length) return;

      /* Determine clip direction based on layout (alternate left/right) */
      var isMediaLeft = !block.classList.contains('keynova-story__block--media-right');
      var clipFrom = isMediaLeft
        ? 'inset(0 100% 0 0)'   /* reveal left → right */
        : 'inset(0 0 0 100%)';  /* reveal right → left */

      var tl = gsap.timeline({
        scrollTrigger: {
          trigger: block,
          start: isDesktop ? 'top 70%' : 'top 80%',
          once: true
        }
      });

      /* Photo: clip-path reveal */
      tl.fromTo(media, {
        clipPath: clipFrom,
        opacity: 0.6
      }, {
        clipPath: 'inset(0 0% 0 0%)',
        opacity: 1,
        duration: 1.0,
        ease: 'power3.out'
      }, 0);

      /* Subtle zoom settle on the image */
      if (img && isDesktop) {
        tl.fromTo(img, {
          scale: 1.08
        }, {
          scale: 1.0,
          duration: 1.4,
          ease: 'power2.out'
        }, 0);
      }

      /* Text: stagger children (overline → title → body) */
      tl.from(textChildren, {
        y: 20,
        opacity: 0,
        stagger: 0.12,
        duration: 0.6,
        ease: 'power2.out'
      }, 0.3);
    });
  }

  /* ----------------------------------------
     APPS — scale + fade reveal on scroll
  ---------------------------------------- */
  /* ----------------------------------------
     APPS — garland reveal with flash-glow on active icons
  ---------------------------------------- */
  function setupApps() {
    if (!document.querySelector('.keynova-apps')) return;
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;

    var items = gsap.utils.toArray('[data-keynova-apps-icon]');
    if (!items.length) return;

    var tl = gsap.timeline({
      scrollTrigger: {
        trigger: '.keynova-apps__grid',
        start: 'top 80%',
        once: true
      }
    });

    // Sequential garland reveal (not batch — ordered DOM sequence)
    tl.from(items, {
      opacity: 0,
      y: 20,
      scale: 0.92,
      stagger: 0.08,
      duration: 0.55,
      ease: 'power2.out'
    });

    // Flash glow on active icons, timed with their reveal position
    items.forEach(function (el, i) {
      if (el.dataset.status !== 'active') return;
      var svg = el.querySelector('.keynova-apps__svg');
      if (!svg) return;

      var enterTime = 0.15 + (i * 0.08);
      tl.to(svg, {
        filter: 'drop-shadow(0 0 14px rgba(255, 119, 0, 0.75))',
        duration: 0.22,
        ease: 'power2.out'
      }, enterTime)
      .to(svg, {
        filter: 'drop-shadow(0 0 0px rgba(255, 119, 0, 0))',
        duration: 0.45,
        ease: 'power2.inOut'
      }, '>-0.05');
    });
  }

  /* ----------------------------------------
     SPECS — staggered slide-in from left
  ---------------------------------------- */
  function setupSpecs() {
    var rows = document.querySelector('.keynova-specs__rows');
    if (!rows) return;
    // V12.9.0 (CP1) — trigger aligne sur le markup reel (.keynova-specs__rows).
    // Avant : '.keynova-specs__grid' n'existait pas depuis le refactor V11.2.6
    // (flow continu multi-column, suppression des wrappers .__group), d'ou le
    // warning ScrollTrigger "Element not found".
    gsap.from('.keynova-specs__row', {
      opacity: 0,
      x: -16,
      stagger: 0.04,
      duration: 0.5,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: rows,
        start: 'top 80%',
        once: true
      }
    });
  }

  /* ----------------------------------------
     V11.3.2 — chantier 2 : Specs collapse mobile.
     Bouton "Voir tous les specs" toggle .is-expanded sur .__rows + .__toggle.
     Audit Osayan post V11.3.1 : sur mobile, 28 elements specs = section
     tres longue, scroll fatiguant. Solution : ~2 premiers groupes visibles
     par defaut (CSS max-height 800px + mask-image fade), bouton expand
     revele le reste (max-height 4000px + mask-image none). aria-expanded
     synchronise pour a11y. Pas de bypass mobile JS : si user resize, le
     state suit. Bouton hidden desktop via CSS (display none > 968px).
  ---------------------------------------- */
  function setupSpecsCollapse() {
    var toggle = document.querySelector('[data-keynova-specs-toggle]');
    var rows = document.querySelector('[data-keynova-specs-rows]');
    if (!toggle || !rows) return;

    toggle.addEventListener('click', function () {
      var isExpanded = rows.classList.toggle('is-expanded');
      toggle.classList.toggle('is-expanded', isExpanded);
      toggle.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
    });
  }

  /* ----------------------------------------
     CTA FINAL — V11.2.3 chantier 5.
     Consolidation de setupCTA + setupCtaReveal V11.2.2 (qui se battaient sur
     l'animation du form : setupCTA animait le form en bloc, setupCtaReveal
     animait ses enfants individuellement). Un seul stagger ici sur 6 elements
     dans l'ordre narratif : H2 -> sub -> prix -> variantes -> ATC -> trust.
     Active aussi le glow radial coin haut-droite (.is-active) et la ligne
     accent verticale (.is-active, draws-in via CSS transition height).
     Parallax photo via GSAP scrub (coherent avec Bureau V11.2.3).
  ---------------------------------------- */
  function setupCtaFinal() {
    var cta = document.querySelector('.keynova-cta');
    if (!cta) return;

    var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // V11.2.6 — glow + accent-line-horizontal retires.
    // V11.2.7 — chantier 6 : accent-line vertical retire du markup egalement
    // (parasite vestige V11.2.3). Seules anims prix slide V11.2.5 conservees.
    var priceCompare = cta.querySelector('.keynova-cta__price-compare');
    var priceCurrent = cta.querySelector('.keynova-cta__price-current');
    var photo = cta.querySelector('.keynova-cta__image, [data-keynova-cta-image]');
    var content = cta.querySelector('.keynova-cta__content');

    var revealElements = [
      cta.querySelector('.keynova-cta__title'),
      cta.querySelector('.keynova-cta__sub'),
      cta.querySelector('.keynova-cta__variants'),
      cta.querySelector('.keynova-cta__atc-btn'),
      cta.querySelector('.keynova-cta__trust')
    ].filter(Boolean);

    if (prefersReduced) {
      if (priceCompare) priceCompare.classList.add('is-active');
      if (priceCurrent) priceCurrent.classList.add('is-active');
      return;
    }

    // V11.2.6 — Photo : zoom-in fade au scroll-in (vs parallax continu V11.2.5).
    // Slow zoom 1.08 -> 1.0 + opacity 0 -> 1 sur 1.2s, une fois (pas de scrub).
    // Le mouvement subtil suffit a faire "arriver" la photo sans surcharger
    // le climax visuel.
    if (photo) {
      gsap.from(photo, {
        scale: 1.08,
        opacity: 0,
        duration: 1.2,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: cta,
          start: 'top 80%',
          once: true
        }
      });
    }

    // V11.2.6 — Bloc transactionnel : arrivee douce y 40 + opacity 0 + scale 0.96
    // sur 0.8s. Effet "le bloc se materialise" cale apres le zoom-in photo.
    if (content) {
      gsap.from(content, {
        y: 40,
        opacity: 0,
        scale: 0.96,
        duration: 0.8,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: cta,
          start: 'top 75%',
          once: true
        }
      });
    }

    if (revealElements.length) {
      gsap.set(revealElements, { opacity: 0, y: 16 });

      ScrollTrigger.create({
        trigger: cta,
        start: 'top 70%',
        once: true,
        onEnter: function () {
          gsap.to(revealElements, {
            opacity: 1,
            y: 0,
            duration: 0.6,
            stagger: 0.12,
            ease: 'power2.out'
          });
          if (priceCompare) {
            setTimeout(function () { priceCompare.classList.add('is-active'); }, 600);
          }
          if (priceCurrent) {
            setTimeout(function () { priceCurrent.classList.add('is-active'); }, 800);
          }
        }
      });

      // Safety net 2.5s : pattern identique aux autres setups V11.2.x.
      setTimeout(function () {
        revealElements.forEach(function (el) {
          if (parseFloat(getComputedStyle(el).opacity) < 0.1) {
            gsap.set(el, { opacity: 1, y: 0 });
          }
        });
        if (priceCompare && !priceCompare.classList.contains('is-active')) priceCompare.classList.add('is-active');
        if (priceCurrent && !priceCurrent.classList.contains('is-active')) priceCurrent.classList.add('is-active');
      }, 2500);
    } else {
      if (priceCompare) priceCompare.classList.add('is-active');
      if (priceCurrent) priceCurrent.classList.add('is-active');
    }
  }

  /* ----------------------------------------
     VALUE CAROUSEL — mobile dots indicators
     (syncs active dot with scroll-snap position)
  ---------------------------------------- */
  function setupValueCarousel() {
    var grid = document.querySelector('[data-keynova-value-grid]');
    var dotsContainer = document.querySelector('[data-keynova-value-dots]');
    if (!grid || !dotsContainer) return;
    if (!window.matchMedia('(max-width: 768px)').matches) return;

    var dots = Array.prototype.slice.call(dotsContainer.querySelectorAll('.keynova-value__dot'));
    var blocks = Array.prototype.slice.call(grid.querySelectorAll('[data-keynova-value-block]'));
    if (!dots.length || !blocks.length) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.intersectionRatio < 0.6) return;
        var idx = blocks.indexOf(entry.target);
        if (idx < 0) return;
        dots.forEach(function (d, i) {
          d.classList.toggle('is-active', i === idx);
        });
      });
    }, {
      root: grid,
      threshold: [0.6, 0.85]
    });

    blocks.forEach(function (b) { observer.observe(b); });
  }

  /* ----------------------------------------
     STICKY ATC — show when hero ATC is scrolled out;
     hide when CTA finale visible (V10.7.1 FIX 1.4).
  ---------------------------------------- */
  function setupStickyATC() {
    var hero = document.querySelector('[data-keynova-hero]');
    var stickyBar = document.querySelector('[data-keynova-sticky-atc]');
    var ctaFinal = document.querySelector('.keynova-cta');
    if (!hero || !stickyBar) return;

    var heroVisible = true;
    // V14.1.2 (§2.1) — logique POSITIONNELLE REVERSIBLE. Remplace le latch
    // permanent V14 (hasReachedCta). Le sticky ATC est visible UNIQUEMENT dans
    // la zone de lecture : hero hors viewport ET CTA finale encore SOUS le
    // viewport. Quand la CTA finale (.keynova-cta) atteint le bas du viewport —
    // puis le footer en dessous — on masque ; en remontant, le sticky revient.
    function inReadingZone() {
      if (!ctaFinal) return true; // pas de CTA finale => zone de lecture jusqu'en bas
      return ctaFinal.getBoundingClientRect().top > window.innerHeight;
    }

    function updateSticky() {
      // V10.7.1 — FIX 1.4 : sticky visible UNIQUEMENT si hero hors viewport ET
      // CTA finale pas encore atteinte (reversible : revient en remontant).
      if (!heroVisible && inReadingZone()) {
        stickyBar.classList.add('is-visible');
      } else {
        stickyBar.classList.remove('is-visible');
      }
    }

    var heroObserver = new IntersectionObserver(function (entries) {
      heroVisible = entries[0].isIntersecting;
      updateSticky();
    }, { threshold: 0, rootMargin: '0px' });
    heroObserver.observe(hero);

    if (ctaFinal) {
      // threshold 0 (vs 0.1 en V14) : le firing coincide avec le passage du haut
      // de la CTA finale au bas du viewport = bascule de inReadingZone().
      var ctaObserver = new IntersectionObserver(function () {
        updateSticky();
      }, { threshold: 0, rootMargin: '0px' });
      ctaObserver.observe(ctaFinal);
    }

    /* V10.6.1 — Bloc fallback ctaATC supprime (selecteur orphan apres
       harmonisation V10.6 du bouton ATC sur .xn-btn--primary). */
  }

  /* ----------------------------------------
     FAQ — close other <details> when one opens
  ---------------------------------------- */
  function setupFAQClosing() {
    var items = document.querySelectorAll('.keynova-faq__item');
    items.forEach(function (item) {
      item.addEventListener('toggle', function () {
        if (!this.open) return;
        items.forEach(function (other) {
          if (other !== item && other.open) {
            other.open = false;
          }
        });
      });
    });
  }

  /* ----------------------------------------
     Bootstrap — wait for DOM if needed
  ---------------------------------------- */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

/* ============================================================
   V10.6 LOT 2 — DESIGNER : IIFE clip-path 3 cases value (Task 18)
   ============================================================ */

// V10.6.1 — Reveal stagger 3 cases value (delta 0.18s).
// Fallback V10.5.2 supprime : il declenchait avant le stagger et masquait l'effet.
// GSAP-undefined : reveal synchrone immediat. Safety net 4s contre ScrollTrigger non firing.
(function initValueReveal() {
  if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
    document.querySelectorAll('.keynova-value__block').forEach(function(b) { b.classList.add('is-revealed'); });
    return;
  }
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    document.querySelectorAll('.keynova-value__block').forEach(function(b) { b.classList.add('is-revealed'); });
    return;
  }
  var blocks = document.querySelectorAll('.keynova-value__block');
  if (!blocks.length) return;
  blocks.forEach(function(b, i) {
    ScrollTrigger.create({
      trigger: b, start: 'top 82%', once: true,
      onEnter: function() { setTimeout(function() { b.classList.add('is-revealed'); }, i * 180); }
    });
  });
  // V10.6.1 safety net — force reveal apres 4s si ScrollTrigger n'a pas fire.
  setTimeout(function() {
    document.querySelectorAll('.keynova-value__block:not(.is-revealed)').forEach(function(b) {
      b.classList.add('is-revealed');
    });
  }, 4000);
})();

// ============================================
// V11.1.0 — Manifeste scroll-reveal sequence
// IntersectionObserver (pattern V11.0.1 — pas de dependance GSAP/ScrollTrigger).
// Threshold 0.4 + rootMargin -10% pour declencher quand la section est bien en vue.
// data-reveal-delay (ms) sequence narrative inter-phrase.
// ============================================
(function () {
  var lines = document.querySelectorAll('.xn-manifeste__line');
  if (!lines.length) return;

  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (prefersReduced) {
    lines.forEach(function (line) { line.classList.add('is-revealed'); });
    return;
  }

  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        var delay = parseInt(entry.target.dataset.revealDelay || '0', 10);
        setTimeout(function () {
          entry.target.classList.add('is-revealed');
        }, delay);
        observer.unobserve(entry.target);
      }
    });
  }, {
    threshold: 0.4,
    rootMargin: '0px 0px -10% 0px'
  });

  lines.forEach(function (line) { observer.observe(line); });
})();

// V11.2.3 — IIFE parallax legacy retire (anciennement scroll listener + rAF
// + transform translateY/scale(1.06) sur .xn-bureau__media[data-parallax]).
// Conflit avec le scrub GSAP de setupBureau qui ecrit aussi transform sur
// .xn-bureau__img : le dernier writer gagnait, et la photo restait figee
// a scale(1.06) constant au lieu de zoomer doucement. Tout est repris
// proprement par setupBureau (cf. plus haut dans ce fichier).
